/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * IMSI / ICCID manuel düzenleme (PATCH /api/sim-kartlari/{id})
 */

import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import api from "utils/api";

/** Sadece rakamlar (yapıştırma dahil harf/boşluk elenir). */
function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

export default function SimIdentifiersEditModal({ sim, isOpen, onClose, onSaved }) {
  const toast = useToast();
  const [imsi, setImsi] = useState("");
  const [iccid, setIccid] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && sim) {
      setImsi(digitsOnly(sim.imsi));
      setIccid(digitsOnly(sim.iccid));
    }
  }, [isOpen, sim]);

  const handleSave = async () => {
    if (!sim?.id) return;
    const imsiTrim = digitsOnly(imsi);
    const iccidTrim = digitsOnly(iccid);
    if (imsiTrim.length < 10 || iccidTrim.length < 10) {
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/api/sim-kartlari/${sim.id}`, {
        imsi: imsiTrim,
        iccid: iccidTrim,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          typeof data?.detail === "string"
            ? data.detail
            : Array.isArray(data?.detail)
            ? data.detail.map((d) => d.msg || d).join(" ")
            : "Güncelleme başarısız."
        );
      }
      onSaved?.(data);
      toast({
        title: "Güncellendi",
        description: "IMSI ve ICCID kaydedildi.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      toast({
        title: "Kayıt başarısız",
        description: e.message || "Kayıt sırasında hata oluştu.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>IMSI / ICCID düzenle</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {sim?.tel_no && (
              <Text fontSize="sm" color="gray.600">
                Telefon: <strong>{sim.tel_no}</strong>
              </Text>
            )}
            <FormControl isRequired>
              <FormLabel fontSize="sm">ICCID</FormLabel>
              <Input
                value={iccid}
                inputMode="numeric"
                autoComplete="off"
                onChange={(e) => setIccid(digitsOnly(e.target.value))}
                placeholder="Sadece rakam"
                fontFamily="mono"
                size="sm"
              />
              <FormHelperText>Sadece rakam; en az 10 hane.</FormHelperText>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">IMSI</FormLabel>
              <Input
                value={imsi}
                inputMode="numeric"
                autoComplete="off"
                onChange={(e) => setImsi(digitsOnly(e.target.value))}
                placeholder="Sadece rakam"
                fontFamily="mono"
                size="sm"
              />
              <FormHelperText>Sadece rakam; en az 10 hane.</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={digitsOnly(iccid).length < 10 || digitsOnly(imsi).length < 10}
          >
            Kaydet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
